/**
 * ── One candidate's interview feedback (the 📋 chip) ─────────────────────────
 *
 * The written feedback from every round, newest first, fetched ONLY when the candidate panel is
 * opened. Nothing here is part of the board payload, so a board with fifty cards costs nothing —
 * the point of the whole panel is that a recruiter stops switching to Lever for context, not that
 * the board gets slower to carry context nobody opened.
 *
 * Lives next to taCandidateEmailsApi.ts for the same reason that one does: taActionBoardApi is at
 * its line budget.
 *
 * ⚠️ This response carries SCORES ("3 - Hire"), which no drafting path may. See the same warning
 * on proxy-server/lib/candidateFeedback.js: the textarea-only rule governs what reaches a model or
 * a candidate-facing email, and it is unchanged. This is a recruiter reading their own screen.
 */
import { authenticatedFetch } from '../../../shared/utils/authenticatedFetch';
import { logAnalyticsEvent } from '../../../analytics/nativeAnalytics';

const PROXY_URL =
  import.meta.env.VITE_PROXY_URL ||
  'https://proxy-prod-people-520260363689.europe-west3.run.app';
const BASE = `${PROXY_URL}/api/ta-action-board`;

/** One written answer on a scorecard. Rendered as TEXT, never as markup. */
export interface FeedbackAnswer {
  label: string;
  value: string;
}

/** One submitted scorecard — one interviewer, one round. */
export interface FeedbackRound {
  id: string;
  /** The Lever form's own name, which is how recruiters say which round it was. */
  form: string;
  interviewer: string | null;
  completedAt: number | null;
  /** The overall verdict as Lever stores it, e.g. "3 - Hire". Null when nobody rated. */
  rating: string | null;
  answers: FeedbackAnswer[];
}

export interface CandidateFeedback {
  /** Newest first. Rounds nobody submitted are not here at all. */
  rounds: FeedbackRound[];
  /** The newest round's form name, or null when there is no feedback yet. */
  latestRound: string | null;
  /** The latest round's distinct verdict words: one when the panel agreed, both when it did not. */
  verdicts: string[];
}

/** A deliberate refusal (403) — shown as a sentence, never retried. */
export class FeedbackRefusedError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'FeedbackRefusedError';
  }
}

export async function fetchCandidateFeedback(input: {
  opportunityId: string;
  boardEmail: string;
}): Promise<CandidateFeedback> {
  const qs = new URLSearchParams({ opportunityId: input.opportunityId, board: input.boardEmail });
  try {
    const res = await authenticatedFetch(`${BASE}/candidate-feedback?${qs}`, {
      // A card left over from a board switch is an expected answer, not an error to report.
      suppressErrorStatuses: [403],
    });
    if (res.status === 403) {
      const body = (await res.json().catch(() => ({}))) as { error?: string };
      void logAnalyticsEvent('candidate_feedback_opened', { result: 'not_on_board' });
      throw new FeedbackRefusedError(body.error || 'This candidate is not on your board');
    }
    const data = (await res.json()) as CandidateFeedback;
    void logAnalyticsEvent('candidate_feedback_opened', {
      result: 'ok',
      rounds: data.rounds.length,
      has_verdict: data.verdicts.length > 0,
    });
    return data;
  } catch (error) {
    if (!(error instanceof FeedbackRefusedError)) {
      void logAnalyticsEvent('candidate_feedback_opened', { result: 'error' });
    }
    throw error;
  }
}
